var _withMatchMedia;
import classNames from 'classnames';
import { inject } from 'mobx-react';
import PropTypes from 'prop-types';
import React, { createContext } from 'react';
import { BackendSourceOptions } from '@udemy/browse-event-tracking';
import { trackGenericCourseClick, TrackImpression } from '@udemy/event-tracking';
import { withFunnelLog } from '@udemy/funnel-tracking';
import { withMatchMedia } from '@udemy/hooks';
import { Icon } from '@udemy/icons';
import { CourseCardBadges, CourseCardDetails, CourseCardImage, CourseCardInstructors, CourseCardRatings, CourseCardTitle, detailsPropsFrom, imagePropsFrom, instructorsPropsFrom, ratingsPropsFrom } from '@udemy/react-card-components';
import { withFeatures } from '@udemy/react-experimentation-provider';
import { PlayOverlay } from '@udemy/react-structure-components';
import { COURSE_BADGE_GROW_WITH_GOOGLE } from '../course-badges/constants';
import { BrowseCourseBadgeHandler } from '../course-badges/course-badges.react-component';
import { withBrowseCourseBadgesContext } from '../course-badges/with-browse-course-badges-context';
import { CourseCardSeoInfo } from '../course-card-seo-info/course-card-seo-info.react-component';
import { pricePropsWithTrackingFrom } from '../course-price-events/course-card-price-events';
import { PersonalPlanBadge } from '../personal-plan-badge/personal-plan-badge.react-component';
import { PrefetchingWrapper } from '../prefetching-wrapper/prefetching-wrapper.react-component';
import { StaticPriceText } from '../price-text/static-price-text.react-component';
import styles from "./browse-course-card.module.css";
import { CourseCardDebug } from './course-card-debug.react-component';
import { trackCourseClickEvent } from './course-card-tracking';
import { CourseCardWithUBGuard } from './course-card-with-ub-guard.react-component';

/**
 * Component to render status text for Grow with Google courses.
 */
const GrowWithGoogleStatusText = () => {
  return /*#__PURE__*/React.createElement("div", {
    className: styles['grow-with-google-status']
  }, /*#__PURE__*/React.createElement("span", {
    className: "ud-text-bold"
  }, "See course for details"));
};
GrowWithGoogleStatusText.displayName = 'GrowWithGoogleStatusText';

/**
 * Context to specify an alternate course card component to render in `BrowseCourseCard`.
 *
 * It must support the same prop interface as `@udemy/react-card-components#CourseCard`.
 *
 * Note: remove this context when we are no longer experimenting with an alternate
 * course card design. It is otherwise unnecessary.
 */
export const BrowseCourseCardContext = /*#__PURE__*/createContext({
  cardComponent: CourseCardWithUBGuard
});
const defaultSlotRenderFunc = (Component, props) => /*#__PURE__*/React.createElement(Component, props);

/**
 * Props supplied by `withMatchMedia`
 * @internal
 */

class InternalBrowseCourseCard extends React.Component {
  constructor(...args) {
    super(...args);
    this.trackImpression = () => {
      const badgeHandler = new BrowseCourseBadgeHandler(this.props.course, this.props.browseBadgeHandlerOptions);
      const {
        trackImpressionFunc,
        relatedSourceId,
        relatedSourceType,
        ...trackingContext
      } = this.props.trackingContext;
      trackImpressionFunc === null || trackImpressionFunc === void 0 || trackImpressionFunc({
        item: {
          ...this.props.course,
          visibleBadgeFamilies: badgeHandler.getBadgeFamilies()
        }
      }, trackingContext, {
        relatedSourceId,
        relatedSourceType
      });
    };
    this.trackClick = event => {
      const {
        trackingClickCallbackFunc,
        course,
        browseBadgeHandlerOptions
      } = this.props;
      const badgeHandler = new BrowseCourseBadgeHandler(course, browseBadgeHandlerOptions);
      const {
        index,
        uiRegion
      } = this.props.trackingContext;
      trackGenericCourseClick({
        courseId: course.id,
        // Assert that at least *one* of these two tracking IDs will be defined
        courseTrackingId: course.frontendTrackingId || course.tracking_id,
        componentName: 'courseCard'
      });
      trackCourseClickEvent({
        ...course,
        visibleBadgeFamilies: badgeHandler.getBadgeFamilies()
      }, index, BackendSourceOptions.DISCOVERY, uiRegion);
      if (trackingClickCallbackFunc) {
        trackingClickCallbackFunc(event);
      }
    };
    this.handleMouseMiddleClick = event => {
      // Means mouse middle click
      if (event.button === 1) {
        this.trackClick(event);
      }
    };
    /**
     * Renders instructor content with a Google icon for Grow with Google courses.
     * For regular courses, renders the default instructor content.
     */
    this.localRenderInstructorContent = isGrowWithGoogleCourse => {
      const {
        course,
        renderInstructorContent
      } = this.props;
      const instructorProps = instructorsPropsFrom({
        course
      });
      if (isGrowWithGoogleCourse) {
        // Wrap the instructor content with a Google icon
        return /*#__PURE__*/React.createElement("div", {
          className: styles['instructor-with-google-icon']
        }, /*#__PURE__*/React.createElement(Icon, {
          size: "xxxsmall",
          glyph: "google",
          label: ""
        }), renderInstructorContent === null || renderInstructorContent === void 0 ? void 0 : renderInstructorContent(CourseCardInstructors, instructorProps));
      }
      return renderInstructorContent === null || renderInstructorContent === void 0 ? void 0 : renderInstructorContent(CourseCardInstructors, instructorProps);
    };
  }
  render() {
    var _experimentationResul, _course$contentCollec;
    const {
      children,
      course,
      cta,
      showBadges,
      titleClass,
      numReviewsText,
      priceProps,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      url,
      renderCourseImage,
      // renderInstructorContent is used in localRenderInstructorContent method
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      renderInstructorContent,
      renderPriceText,
      renderCourseTitle,
      renderRatings,
      renderDetails,
      renderCourseBadges,
      trackingContext,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      trackingClickCallbackFunc,
      isUserEnrolled,
      showPersonalPlanBadge,
      browseBadgeHandlerOptions,
      experimentationResult,
      trackMiddleClick,
      isMobile,
      ...props
    } = this.props;
    const personalPlanExplicitEnrollAssignment = experimentationResult === null || experimentationResult === void 0 || (_experimentationResul = experimentationResult.featureVariantAssignments) === null || _experimentationResul === void 0 ? void 0 : _experimentationResul.get('personal_plan_course_enrollment');
    const personalPlanExplicitEnroll = personalPlanExplicitEnrollAssignment === null || personalPlanExplicitEnrollAssignment === void 0 ? void 0 : personalPlanExplicitEnrollAssignment.configuration.enablePersonalPlanEnrollments;
    const clickUrl = course.is_in_user_subscription && (!personalPlanExplicitEnroll || isUserEnrolled) ? course.learn_url : course.url;
    const localRenderImage = () => {
      // Augment className in image props
      const baseImageProps = imagePropsFrom({
        course
      });
      const imageProps = {
        ...baseImageProps,
        className: classNames(baseImageProps.className, styles.image)
      };
      if (isUserEnrolled) {
        return /*#__PURE__*/React.createElement(React.Fragment, null, renderCourseImage === null || renderCourseImage === void 0 ? void 0 : renderCourseImage(CourseCardImage, imageProps), /*#__PURE__*/React.createElement("div", {
          className: styles['opacity-overlay-dark']
        }), /*#__PURE__*/React.createElement(PlayOverlay, {
          className: styles['enrolled-play-overlay-mobile']
        }));
      }
      if (course.is_in_personal_plan_collection && showPersonalPlanBadge) {
        return /*#__PURE__*/React.createElement(React.Fragment, null, renderCourseImage === null || renderCourseImage === void 0 ? void 0 : renderCourseImage(CourseCardImage, imageProps), /*#__PURE__*/React.createElement(PersonalPlanBadge, null));
      }
      return renderCourseImage === null || renderCourseImage === void 0 ? void 0 : renderCourseImage(CourseCardImage, imageProps);
    };
    const localRenderTitle = () => {
      const titleContent = /*#__PURE__*/React.createElement(React.Fragment, null, course.title, /*#__PURE__*/React.createElement(CourseCardSeoInfo, {
        course: course
      }));
      const openInNewTab = !isMobile;
      /* eslint-disable react/jsx-no-target-blank */
      /* Since the course card only opens to Udemy-controlled pages (i.e, CLPs), there's
       * no security concern. We want to allow the referrer for tracking purposes.
       */
      const newTabProps = openInNewTab ? {
        target: '_blank',
        rel: 'noopener'
      } : {};
      return renderCourseTitle === null || renderCourseTitle === void 0 ? void 0 : renderCourseTitle(CourseCardTitle, {
        className: titleClass,
        children: clickUrl ? /*#__PURE__*/React.createElement("a", Object.assign({
          href: clickUrl
        }, newTabProps), titleContent) : /*#__PURE__*/React.createElement("span", null, titleContent)
      });
    };
    const localRenderBadges = () => {
      // Filter out the Grow with Google badge for BrowseCourseCard
      const filteredBadgeHandlerOptions = {
        ...browseBadgeHandlerOptions,
        filter: badge => {
          // First apply the custom filter if it exists
          const customFilterPasses = browseBadgeHandlerOptions !== null && browseBadgeHandlerOptions !== void 0 && browseBadgeHandlerOptions.filter ? browseBadgeHandlerOptions.filter(badge, course) : true;
          // Then exclude the Google badge
          return customFilterPasses && badge !== COURSE_BADGE_GROW_WITH_GOOGLE;
        }
      };
      const badgeHandler = new BrowseCourseBadgeHandler(course, filteredBadgeHandlerOptions);
      const WrappedCourseCardBadges = ({
        children
      }) => {
        return /*#__PURE__*/React.createElement("div", {
          className: styles['wrapped-course-badges']
        }, /*#__PURE__*/React.createElement(CourseCardBadges, null, children));
      };
      return renderCourseBadges === null || renderCourseBadges === void 0 ? void 0 : renderCourseBadges(WrappedCourseCardBadges, {
        children: badgeHandler.getBadgeComponents()
      });
    };
    const isBuyable = !(course.is_in_user_subscription || isUserEnrolled);
    const isGrowWithGoogleCourse = !!((_course$contentCollec = course.contentCollectionInfo) !== null && _course$contentCollec !== void 0 && _course$contentCollec.isInGooglePartnerCollection);
    const CourseCard = this.context.cardComponent;
    const courseCardLink = /*#__PURE__*/React.createElement(PrefetchingWrapper, {
      url: clickUrl
    }, /*#__PURE__*/React.createElement(CourseCard, Object.assign({}, props, {
      badges: isBuyable && showBadges && localRenderBadges(),
      details: renderDetails === null || renderDetails === void 0 ? void 0 : renderDetails(CourseCardDetails, detailsPropsFrom({
        course
      })),
      headline: course.headline,
      image: localRenderImage(),
      instructors: this.localRenderInstructorContent(isGrowWithGoogleCourse),
      onClick: this.trackClick,
      onContextMenu: this.trackClick,
      onMouseUp: trackMiddleClick ? this.handleMouseMiddleClick : undefined,
      ratings: renderRatings === null || renderRatings === void 0 ? void 0 : renderRatings(CourseCardRatings, ratingsPropsFrom({
        course,
        numReviewsText
      })),
      price: isGrowWithGoogleCourse ? /*#__PURE__*/React.createElement(GrowWithGoogleStatusText, null) : isBuyable && (renderPriceText === null || renderPriceText === void 0 ? void 0 : renderPriceText(StaticPriceText, pricePropsWithTrackingFrom({
        course,
        priceTextProps: priceProps
      }))),
      title: localRenderTitle()
    }), children, cta && /*#__PURE__*/React.createElement("div", {
      className: styles.cta
    }, cta), /*#__PURE__*/React.createElement(CourseCardDebug, {
      course: course
    })));
    if (!(trackingContext !== null && trackingContext !== void 0 && trackingContext.trackImpressionFunc)) {
      return courseCardLink;
    }
    return /*#__PURE__*/React.createElement(TrackImpression, {
      trackFunc: this.trackImpression
    }, courseCardLink);
  }
}

/**
 * Browse version of CourseCard wraps the base CourseCard with a course link, and
 * adds tracking for impressions and clicks.
 */
InternalBrowseCourseCard.propTypes = {
  course: PropTypes.object.isRequired,
  size: PropTypes.oneOf(['small', 'medium', 'large', 'wide-4-columns', 'wide-7-columns', 'wide-9-columns', 'wide-10-columns', 'wide-12-columns']),
  width: PropTypes.oneOf(['fixed', 'flexible']),
  className: PropTypes.string,
  titleClass: PropTypes.string,
  numReviewsText: PropTypes.string,
  priceProps: PropTypes.object,
  showBadges: PropTypes.bool,
  showDetails: PropTypes.bool,
  url: PropTypes.string,
  /*
   * Custom rendering functions receive a component and props to
   * render, optionally with customization. This customization pattern
   * comes from an older course card, and these functions are retained
   * here for backwards compatibility for consumers of this component. For
   * context about which components are passed to each of these functions
   * see `https://github.com/udemy/design-system-web/tree/main/packages/react-card-components/src/api-course-card`
   */
  renderCourseImage: PropTypes.func,
  renderInstructorContent: PropTypes.func,
  renderPriceText: PropTypes.func,
  renderCourseTitle: PropTypes.func,
  renderRatings: PropTypes.func,
  renderDetails: PropTypes.func,
  renderCourseBadges: PropTypes.func,
  /**
   * Optional handler if user clicks on CourseCard.
   *
   * @remarks
   * This prop is not recommended for user interaction. It is placed on the enclosing `div`
   * and does not have the associated `role` or keyboard event handlers required for a11y.
   * It can be used for click tracking.
   */
  onClick: PropTypes.func,
  isUserEnrolled: PropTypes.bool,
  trackingContext: PropTypes.shape({
    backendSource: PropTypes.string,
    index: PropTypes.number,
    trackImpressionFunc: PropTypes.func,
    relatedSourceId: PropTypes.string,
    relatedSourceType: PropTypes.string,
    uiRegion: PropTypes.string
  }).isRequired,
  trackingClickCallbackFunc: PropTypes.func,
  trackMiddleClick: PropTypes.bool,
  showPersonalPlanBadge: PropTypes.bool,
  browseBadgeHandlerOptions: PropTypes.shape({
    numberOfBadgesToShow: PropTypes.number,
    filter: PropTypes.func
  })
};
InternalBrowseCourseCard.defaultProps = {
  size: 'medium',
  width: 'flexible',
  className: undefined,
  titleClass: undefined,
  numReviewsText: undefined,
  priceProps: undefined,
  showBadges: true,
  showDetails: true,
  renderCourseImage: defaultSlotRenderFunc,
  renderInstructorContent: defaultSlotRenderFunc,
  renderPriceText: defaultSlotRenderFunc,
  renderCourseTitle: defaultSlotRenderFunc,
  renderRatings: defaultSlotRenderFunc,
  renderDetails: defaultSlotRenderFunc,
  renderCourseBadges: defaultSlotRenderFunc,
  url: undefined,
  onClick: undefined,
  isUserEnrolled: undefined,
  trackingClickCallbackFunc: undefined,
  showPersonalPlanBadge: false,
  trackMiddleClick: false
};
InternalBrowseCourseCard.contextType = BrowseCourseCardContext;
export const BrowseCourseCard = Object.assign((_withMatchMedia = withMatchMedia({
  isMobile: 'mobile-max'
})(withBrowseCourseBadgesContext(withFunnelLog('course')(withFeatures(['personal_plan_course_enrollment'], inject(({
  showPersonalPlanBadge,
  trackingContext = {}
}) => ({
  showPersonalPlanBadge,
  trackingContext
}))(InternalBrowseCourseCard))))
/* This is to satisfy TS, since withFunnelLog has a case where it could potentially return nothing if:
 * 1.) it's unable to construct its store
 * 2.) the component it wraps does not render successfully
 * We don't ever expect that to happen here, since the course card should always render something, but TS makes us handle it....
 */)) !== null && _withMatchMedia !== void 0 ? _withMatchMedia : {}, {
  defaultCardComponent: CourseCardWithUBGuard,
  displayName: 'BrowseCourseCard'
});
//# sourceMappingURL=browse-course-card.react-component.js.map